import { logger } from '@/core/logger';
import { useFocusEffect } from '@react-navigation/native';
import { useEvent } from 'expo';
import { useVideoPlayer, VideoView } from 'expo-video';
import React, { useRef, useState } from 'react';
import { AppState, ImageBackground, Platform, StyleSheet, View, ViewProps } from 'react-native';

export interface BackgroundVideoProps extends Omit<ViewProps, 'style'> {
    uri?: string;
    localSource?: any; // For local videos using require()
    style?: ViewProps['style'];
    videoStyle?: ViewProps['style'];
    contentFit?: 'contain' | 'cover' | 'fill';
    placeholderImage?: any; // Placeholder image while video loads
    onVideoReady?: () => void; // Callback when video is ready to play
    onVideoEnd?: () => void; // Callback when video ends
    loop?: boolean; // Whether the video should loop (default: false)
}

export const BackgroundVideo = React.memo<BackgroundVideoProps>(function BackgroundVideo({
    uri,
    localSource,
    style,
    videoStyle,
    contentFit = 'cover',
    placeholderImage,
    onVideoReady,
    onVideoEnd,
    loop = false,
    children,
    ...props
}) {
    const [isVideoReady, setIsVideoReady] = useState(false);
    const [isPlayerReleased, setIsPlayerReleased] = useState(false);
    const source = uri ? uri : localSource;
    const playerRef = useRef<any>(null);
    const isMountedRef = useRef(true);
    const operationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const player = useVideoPlayer(source, player => {
        try {
            player.loop = loop;
            player.muted = true;
            player.showNowPlayingNotification = false;
            player.play();
            playerRef.current = player;
        } catch (error) {
            logger.warn('BackgroundVideo', 'Failed to initialize video player:', error);
            setIsPlayerReleased(true);
        }
    });

    // Listen to player status changes to detect when video is ready
    const { status } = useEvent(player, 'statusChange', { status: player.status });

    // Listen to playing changes to detect when video ends
    const { isPlaying } = useEvent(player, 'playingChange', { isPlaying: player.playing });

    // Update video ready state when status changes to 'readyToPlay' or 'loading' completes
    React.useEffect(() => {
        if (status === 'readyToPlay' || status === 'loading') {
            setIsVideoReady(true);
            onVideoReady?.();
        }
    }, [status, onVideoReady]);

    // Detect when video ends (stops playing) and call onVideoEnd callback
    React.useEffect(() => {
        if (isPlaying === false && isVideoReady) {
            // Small delay to ensure this is actually the end, not just a pause
            const timer = setTimeout(() => {
                onVideoEnd?.();
            }, 100);
            return () => clearTimeout(timer);
        }
        return undefined; // ✅ Explicit return for all code paths
    }, [isPlaying, isVideoReady, onVideoEnd]);

    // Safe player operations with null checks and debouncing
    const safePlayerOperation = React.useCallback((operation: () => void) => {
        // Don't schedule operations if component is unmounted or player is released
        if (!isMountedRef.current || isPlayerReleased) {
            return;
        }

        // Clear any pending operation
        if (operationTimeoutRef.current) {
            clearTimeout(operationTimeoutRef.current);
        }

        operationTimeoutRef.current = setTimeout(() => {
            // Double-check conditions before executing
            if (!isMountedRef.current || isPlayerReleased || !player || !playerRef.current) {
                return;
            }

            try {
                // Additional check to ensure player is still valid and has required methods
                if (status === 'readyToPlay' &&
                    typeof player.pause === 'function' &&
                    typeof player.play === 'function') {
                    operation();
                } else if (status !== 'readyToPlay') {
                    // Don't mark as released if just not ready yet
                    return;
                } else {
                    logger.warn('BackgroundVideo', 'Player methods not available, marking as released');
                    if (isMountedRef.current) {
                        setIsPlayerReleased(true);
                    }
                }
            } catch (error) {
                logger.warn('BackgroundVideo', 'Video player operation failed:', error);
                if (isMountedRef.current) {
                    setIsPlayerReleased(true);
                }
            }
        }, 100); // 100ms debounce
    }, [player, isPlayerReleased, status]);

    // Handle app state changes (minimize/resume)
    React.useEffect(() => {
        const handleAppStateChange = (nextAppState: string) => {
            if (nextAppState === 'active') {
                // App came to foreground - resume video
                safePlayerOperation(() => player.play());
            } else if (nextAppState === 'background') {
                // App went to background - pause video
                safePlayerOperation(() => player.pause());
            }
        };

        const subscription = AppState.addEventListener('change', handleAppStateChange);

        return () => {
            subscription?.remove();
        };
    }, [safePlayerOperation, player]);

    // Pause video when screen is not focused, resume when focused
    useFocusEffect(
        React.useCallback(() => {
            // Screen is focused - resume video if it was paused and app is active
            if (AppState.currentState === 'active') {
                safePlayerOperation(() => player.play());
            }

            return () => {
                // Screen is unfocused - pause video for performance
                safePlayerOperation(() => player.pause());
            };
        }, [safePlayerOperation, player])
    );

    // Cleanup on unmount
    React.useEffect(() => {
        return () => {
            isMountedRef.current = false;

            // Clear any pending operations
            if (operationTimeoutRef.current) {
                clearTimeout(operationTimeoutRef.current);
                operationTimeoutRef.current = null;
            }

            // Mark player as released to prevent any further operations
            setIsPlayerReleased(true);

            // Don't try to pause the player on cleanup as it may already be released
            // The expo-video library handles its own cleanup
        };
    }, [player, isPlayerReleased]);

    return (
        <View style={[styles.container, style]} {...props}>
            {/* Placeholder image */}
            {(!isVideoReady || isPlayerReleased) && placeholderImage && (
                <ImageBackground
                    source={placeholderImage}
                    style={styles.placeholder}
                    resizeMode="cover"
                />
            )}

            {/* Only render VideoView if player is not released */}
            {!isPlayerReleased && (
                <VideoView
                    style={[styles.video, videoStyle]}
                    player={player}
                    allowsPictureInPicture={false}
                    nativeControls={false}
                    contentFit={contentFit}
                />
            )}
            {children}
        </View>
    );
});

const styles = StyleSheet.create({
    container: {
        flex: 1,
        width: '100%',
        height: '100%',
        // Avoid centering that can leave full-bleed media looking left-shifted
        // when browser zoom out changes the layout/paint size.
        overflow: 'hidden',
    },
    video: {
        position: 'absolute',
        top: 0,
        left: 0,
        bottom: 0,
        right: 0,
        width: '100%',
        height: '100%',
        ...(Platform.OS === 'web'
            ? ({
                objectFit: 'cover',
                objectPosition: 'center',
            } as object)
            : null),
    },
    placeholder: {
        position: 'absolute',
        top: 0,
        left: 0,
        bottom: 0,
        right: 0,
        width: '100%',
        height: '100%',
        ...(Platform.OS === 'web'
            ? ({
                backgroundPosition: 'center',
                backgroundSize: 'cover',
            } as object)
            : null),
    },
});

export default BackgroundVideo;